/** First-open PDF backdrop generation: rasterize every page with pdfjs, place
 * each as a LOCKED image element at its canonical layout rect (coords.ts), and
 * record the layout in `appState.gm_pages`. The PNGs are content-hashed into
 * Excalidraw fileIds (the same SHA-1-of-bytes scheme Excalidraw itself uses)
 * and uploaded to the drawing/files endpoint once — thereafter every open
 * fetches them back by id, and the server's flatten/OCR read gm_pages.
 *
 * Pages rasterize at 2x the layout scale so zooming into the scene stays
 * legible; the element's width/height stay the canonical layout size. */

import * as pdfjs from "pdfjs-dist";
import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";

import { ensureFreshToken } from "@/lib/auth-token";

import { pageLayout, SCENE_SCALE, type PageRect } from "./coords";

pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

const RASTER_OVERSAMPLE = 2;

export interface PdfBackdrop {
  /** Skeleton image elements (convertToExcalidrawElements input), in page order. */
  elementSkeletons: {
    type: "image";
    fileId: string;
    x: number;
    y: number;
    width: number;
    height: number;
    locked: true;
  }[];
  /** fileId -> PNG dataURL for the rendered pages. */
  files: Map<string, string>;
  gmPages: PageRect[];
}

async function sha1Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-1", bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function canvasPngDataUrl(canvas: HTMLCanvasElement): Promise<string> {
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) throw new Error("could not encode a page PNG");
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("could not read a page PNG"));
    reader.readAsDataURL(blob);
  });
}

function dataUrlBytes(dataUrl: string): ArrayBuffer {
  const b64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

/** Render every page of the PDF at `url` into the canonical backdrop. */
export async function buildPdfBackdrop(url: string): Promise<PdfBackdrop> {
  await ensureFreshToken(); // same-origin cookie auth for pdf.js's range requests
  const doc = await pdfjs.getDocument({ url, withCredentials: true }).promise;
  try {
    const sizesPt: { w: number; h: number }[] = [];
    for (let n = 1; n <= doc.numPages; n++) {
      const vp = (await doc.getPage(n)).getViewport({ scale: 1 });
      sizesPt.push({ w: vp.width, h: vp.height });
    }
    const gmPages = pageLayout(sizesPt);

    const elementSkeletons: PdfBackdrop["elementSkeletons"] = [];
    const files = new Map<string, string>();
    for (let n = 1; n <= doc.numPages; n++) {
      const page = await doc.getPage(n);
      const viewport = page.getViewport({ scale: SCENE_SCALE * RASTER_OVERSAMPLE });
      const canvas = document.createElement("canvas");
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("no 2d context for the page raster");
      await page.render({ canvasContext: ctx, viewport }).promise;
      const dataURL = await canvasPngDataUrl(canvas);
      canvas.width = 0; // release the raster buffer promptly
      canvas.height = 0;
      const fileId = await sha1Hex(dataUrlBytes(dataURL));
      files.set(fileId, dataURL);
      const rect = gmPages[n - 1];
      if (!rect) continue; // unreachable: gmPages is built from the same page list
      elementSkeletons.push({
        type: "image",
        fileId,
        x: rect.x,
        y: rect.y,
        width: rect.w,
        height: rect.h,
        locked: true,
      });
    }
    return { elementSkeletons, files, gmPages };
  } finally {
    void doc.destroy();
  }
}
